import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, CreditCard, Banknote, QrCode, Split, CheckCircle, Tag, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { useCartStore } from '../../../stores/cart.store';
import { useAuthStore } from '../../../stores/auth.store';
import { useBranchStore } from '../../../stores/branch.store';
import { useCreateOrder, useAddPaymentDynamic } from '../../../api/mutations/order.mutations';
import { useCalculateEligiblePromotions, type EligiblePromotion } from '../../../api/mutations/promotion.mutations';

interface CheckoutModalProps {
  onClose: () => void;
}

type PaymentMethod = 'cash' | 'card' | 'qr' | 'split';

function CheckoutModal({ onClose }: CheckoutModalProps) {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const selectedBranchId = useBranchStore((state) => state.selectedBranchId);
  const {
    items,
    customerId,
    loyaltyPointsToRedeem,
    appliedPromotion,
    setAppliedPromotion,
    getSubtotal,
    getPromotionDiscount,
    getLoyaltyDiscount,
    getTax,
    getTotal,
    clearCart
  } = useCartStore();
  const createOrderMutation = useCreateOrder();
  const addPaymentMutation = useAddPaymentDynamic();
  const calculateEligibleMutation = useCalculateEligiblePromotions();

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
  const [cashReceived, setCashReceived] = useState<string>('');
  const [cardLast4, setCardLast4] = useState('');
  const [qrReference, setQrReference] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [eligiblePromotions, setEligiblePromotions] = useState<EligiblePromotion[]>([]);
  const [showAllPromotions, setShowAllPromotions] = useState(false);

  const total = getTotal();
  const subtotal = getSubtotal();
  const promoDiscount = getPromotionDiscount();
  const loyaltyDiscount = getLoyaltyDiscount();
  const tax = getTax();

  // Fetch eligible promotions when modal opens
  useEffect(() => {
    const branchId = selectedBranchId || user?.branchId;
    if (!branchId || items.length === 0) return;

    const cartItems = items.map(item => ({
      menuItemId: item.menuItemId,
      categoryId: item.categoryId,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      size: item.size,
    }));

    calculateEligibleMutation.mutate(
      { items: cartItems, branchId, customerId },
      {
        onSuccess: (data) => {
          setEligiblePromotions(data.eligiblePromotions);
          // Auto-apply best promotion if available and not already applied
          if (data.bestAutoApply && !appliedPromotion) {
            setAppliedPromotion({
              promotionId: data.bestAutoApply.promotion._id,
              name: data.bestAutoApply.promotion.name,
              type: data.bestAutoApply.promotion.type,
              discountAmount: data.bestAutoApply.discountAmount,
            });
          }
        },
      }
    );
  }, []);

  const handleSelectPromotion = (promo: EligiblePromotion) => {
    setAppliedPromotion({
      promotionId: promo.promotion._id,
      name: promo.promotion.name,
      type: promo.promotion.type,
      discountAmount: promo.discountAmount,
    });
  };

  const handleRemovePromotion = () => {
    setAppliedPromotion(undefined);
  };

  const getLocalizedName = (name: { en: string; th: string }) => {
    return i18n.language === 'th' ? name.th || name.en : name.en;
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const calculateChange = () => {
    if (paymentMethod !== 'cash') return 0;
    const received = parseFloat(cashReceived) || 0;
    return Math.max(0, received - total);
  };

  const canProcessPayment = () => {
    if (paymentMethod === 'cash') {
      const received = parseFloat(cashReceived) || 0;
      return received >= total;
    }
    if (paymentMethod === 'card') {
      return cardLast4.length === 4;
    }
    if (paymentMethod === 'qr') {
      return qrReference.length > 0;
    }
    return false; // Split payment would need additional logic
  };

  // Handle Enter key to submit payment
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canProcessPayment() && !isProcessing) {
      e.preventDefault();
      handleProcessPayment();
    }
  };

  const handleProcessPayment = async () => {
    // Use selected branch from header dropdown, fallback to user's branch
    const branchId = selectedBranchId || user?.branchId;
    if (!branchId) {
      alert('Please select a branch first');
      return;
    }

    setIsProcessing(true);

    try {
      // Prepare order input
      const orderInput = {
        branchId,
        customerId: customerId,
        items: items.map((item) => ({
          menuItemId: item.menuItemId,
          quantity: item.quantity,
          size: item.size,
          sweetness: item.sweetness,
          modifiers: item.modifiers,
          notes: item.notes,
        })),
        loyaltyPointsToRedeem: loyaltyPointsToRedeem || 0,
        // Include applied promotion
        promotionId: appliedPromotion?.promotionId,
        promotionDiscount: appliedPromotion?.discountAmount,
      };

      // Step 1: Create order
      const order = await createOrderMutation.mutateAsync(orderInput);

      // Step 2: Add payment to order (this triggers inventory deduction)
      const paymentData = {
        method: paymentMethod,
        amount: total,
        ...(paymentMethod === 'card' && cardLast4 ? { cardLast4 } : {}),
        ...(paymentMethod === 'qr' && qrReference ? { qrReference } : {}),
      };

      await addPaymentMutation.mutateAsync({
        orderId: order._id,
        payment: paymentData,
      });

      setOrderNumber(order.orderNumber);
      setIsSuccess(true);

      // Clear cart after 2 seconds and close modal
      setTimeout(() => {
        clearCart();
        onClose();
      }, 2000);
    } catch (error: any) {
      console.error('Payment processing error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Payment failed. Please try again.';
      alert(t('pos.paymentError') || errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const paymentMethods = [
    { id: 'cash' as const, label: t('pos.cash'), icon: Banknote },
    { id: 'card' as const, label: t('pos.card'), icon: CreditCard },
    { id: 'qr' as const, label: t('pos.qr'), icon: QrCode },
    { id: 'split' as const, label: t('pos.split'), icon: Split },
  ];

  if (isSuccess) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="w-full max-w-md rounded-lg bg-card p-8 text-center">
          <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" />
          <h2 className="text-2xl font-bold mb-2">{t('pos.paymentSuccess')}</h2>
          <p className="text-muted-foreground mb-4">
            {t('pos.orderNumber')}: <span className="font-mono font-bold">{orderNumber}</span>
          </p>
          <p className="text-sm text-muted-foreground">{t('pos.redirecting')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-card">
        {/* Header */}
        <div className="sticky top-0 z-10 border-b bg-card p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold">{t('pos.checkout')}</h2>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="rounded-full p-2 hover:bg-secondary tap-highlight-none touch-target disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Order summary */}
          <div className="rounded-lg border p-4 bg-background">
            <h3 className="font-medium mb-3">{t('pos.orderSummary')}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('pos.items')} ({items.reduce((sum, item) => sum + item.quantity, 0)})</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              {promoDiscount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span className="flex items-center gap-1">
                    <Tag className="h-3 w-3" />
                    {appliedPromotion ? getLocalizedName(appliedPromotion.name) : t('pos.promotion')}
                  </span>
                  <span>-{formatPrice(promoDiscount)}</span>
                </div>
              )}
              {loyaltyDiscount > 0 && (
                <div className="flex justify-between text-orange-600">
                  <span>{t('pos.loyaltyPoints')}</span>
                  <span>-{formatPrice(loyaltyDiscount)}</span>
                </div>
              )}
              <div className="flex justify-between text-muted-foreground">
                <span>{t('pos.tax')} (7%)</span>
                <span>{formatPrice(tax)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>{t('pos.total')}</span>
                <span className="text-primary">{formatPrice(total)}</span>
              </div>
            </div>
          </div>

          {/* Available Promotions */}
          {eligiblePromotions.length > 0 && (
            <div className="rounded-lg border p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-green-600" />
                  {t('pos.availablePromotions') || 'Available Promotions'}
                </h3>
                {eligiblePromotions.length > 2 && (
                  <button
                    onClick={() => setShowAllPromotions(!showAllPromotions)}
                    className="text-xs text-green-600 hover:underline flex items-center gap-1"
                  >
                    {showAllPromotions ? t('common.showLess') : `+${eligiblePromotions.length - 2} ${t('common.more')}`}
                    {showAllPromotions ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {(showAllPromotions ? eligiblePromotions : eligiblePromotions.slice(0, 2)).map((promo) => {
                  const isApplied = appliedPromotion?.promotionId === promo.promotion._id;
                  return (
                    <button
                      key={promo.promotion._id}
                      onClick={() => isApplied ? handleRemovePromotion() : handleSelectPromotion(promo)}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        isApplied
                          ? 'bg-green-100 border-green-500 dark:bg-green-900/50'
                          : 'bg-white dark:bg-card hover:border-green-400'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate">{getLocalizedName(promo.promotion.name)}</span>
                            {promo.promotion.highlightBadge && (
                              <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded">
                                {promo.promotion.highlightBadge}
                              </span>
                            )}
                            {isApplied && (
                              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-green-600 text-white rounded">
                                {t('pos.applied') || 'Applied'}
                              </span>
                            )}
                          </div>
                          {promo.promotion.description && (
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">
                              {getLocalizedName(promo.promotion.description as { en: string; th: string })}
                            </p>
                          )}
                        </div>
                        <div className="text-right ml-3 shrink-0">
                          <span className="text-green-600 font-bold">-{formatPrice(promo.discountAmount)}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              {appliedPromotion && (
                <button
                  onClick={handleRemovePromotion}
                  className="mt-2 text-xs text-red-500 hover:underline"
                >
                  {t('pos.removePromotion') || 'Remove promotion'}
                </button>
              )}
            </div>
          )}

          {/* Payment method selection */}
          <div className="space-y-3">
            <label className="text-sm font-medium">{t('pos.paymentMethod')}</label>
            <div className="grid grid-cols-2 gap-3">
              {paymentMethods.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setPaymentMethod(id)}
                  disabled={id === 'split'} // TODO: Implement split payment
                  className={`rounded-lg border p-4 flex flex-col items-center gap-2 transition-colors touch-target tap-highlight-none disabled:opacity-50 disabled:cursor-not-allowed ${
                    paymentMethod === id
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background hover:bg-secondary'
                  }`}
                >
                  <Icon className="h-6 w-6" />
                  <span className="font-medium">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Payment details based on method */}
          {paymentMethod === 'cash' && (
            <div className="space-y-3">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t('pos.cashReceived')}</label>
                <input
                  type="number"
                  value={cashReceived}
                  onChange={(e) => setCashReceived(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="0"
                  className="w-full rounded-lg border bg-background px-4 py-3 text-lg font-mono"
                  autoFocus
                />
              </div>
              {parseFloat(cashReceived) > 0 && (
                <div className="rounded-lg bg-secondary p-4">
                  <div className="flex justify-between text-lg">
                    <span className="font-medium">{t('pos.change')}</span>
                    <span className="font-bold text-primary">{formatPrice(calculateChange())}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {paymentMethod === 'card' && (
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('pos.cardLast4')}</label>
              <input
                type="text"
                value={cardLast4}
                onChange={(e) => setCardLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
                onKeyDown={handleKeyDown}
                placeholder="1234"
                maxLength={4}
                className="w-full rounded-lg border bg-background px-4 py-3 text-lg font-mono"
                autoFocus
              />
            </div>
          )}

          {paymentMethod === 'qr' && (
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('pos.qrReference')}</label>
              <input
                type="text"
                value={qrReference}
                onChange={(e) => setQrReference(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="REF123456"
                className="w-full rounded-lg border bg-background px-4 py-3 text-lg font-mono"
                autoFocus
              />
            </div>
          )}

          {paymentMethod === 'split' && (
            <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
              <Split className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>{t('pos.splitPaymentComingSoon') || 'Split payment coming soon'}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 border-t bg-card p-4">
          <button
            onClick={handleProcessPayment}
            disabled={!canProcessPayment() || isProcessing}
            className="w-full rounded-lg bg-primary py-4 font-bold text-primary-foreground hover:bg-primary/90 transition-colors touch-target tap-highlight-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? t('pos.processing') : t('pos.completePayment')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CheckoutModal;
